import React, { Component, Fragment, useRef } from 'react';
import { Table } from 'antd';
import cx from 'classnames';
import { filter, find, findIndex, isNil, pullAll, uniq } from 'lodash';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { comparePrePropsAndNextProps } from '../../../lib/common';
import { PAGE_SIZE_OPTIONS } from '../../../constants/service/common/utilConstants';
import './Table.scss';

let dragingIndex = -1;

const DraggableBodyRow = ({ index, id, moverow, candrag, className, style, ...restProps }) => {
  const ref = useRef(null);
  const [{ isDragging }, drag] = useDrag({
    type: 'row',
    item: () => ({ type: 'row', id, index }),
    canDrag: candrag,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: 'row',
    hover(item) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      moverow(item.id, id);
      item.index = hoverIndex;
    },
  });

  drag(drop(ref));

  let classNames = className;
  if (isDragging) {
    classNames += ' dragging';
  }
  if (isOver) {
    if (index > dragingIndex) {
      classNames += ' drop-over-downward';
    }
    if (index < dragingIndex) {
      classNames += ' drop-over-upward';
    }
  }

  return (
    <tr ref={ref} className={classNames} style={{ ...style, cursor: candrag ? 'move' : 'default' }} {...restProps} />
  );
};

class CustomTable extends Component {
  /**
   *  Table Component 의 변경되는 props 와 state 가 있을 경우에만,
   *  render 메소드를 호출할 수 있도록 함
   */
  shouldComponentUpdate = (nextProps, nextState) => {
    return comparePrePropsAndNextProps([
      [nextProps.list, this.props.list],
      [nextProps.pagination, this.props.pagination],
      [nextProps.selectedList, this.props.selectedList],
      [
        nextProps.columns({
          filterOption: nextProps.filterOption || {},
          sortOption: nextProps.sortOption || {},
          onFilter: this.onFilter,
        }),
        this.props.columns({
          filterOption: this.props.filterOption || {},
          sortOption: this.props.sortOption || {},
          onFilter: this.onFilter,
        }),
      ],
      [nextState, this.state],
    ]);
  };

  /**
   *  Table 의 pagination, filter, sorter 를 변경할 때 실행되는 callback
   */
  onChange = (pagination, filters, sorter) => {
    const { filterOption, getList } = this.props;
    if (!getList) return;

    /**
     *  sortOption 설정하기
     */
    const updatedSortOption = {};

    const sortKey = sorter.columnKey ?? sorter.column?.key ?? sorter.field;
    if (sortKey && sorter.order) {
      updatedSortOption[sortKey] = sorter.order;
    }

    getList({
      filterOption,
      sortOption: updatedSortOption,
      pagination: {
        pageSize: pagination.pageSize,
        pageNum: pagination.current - 1,
      },
    });
  };

  /**
   *  Checkbox 를 사용하려면 Table Component 에
   *  rowSelection props 를 사용해야함
   */
  rowSelection = (onCheck, selectedList) => {
    let rowSelection = null;
    if (onCheck || selectedList) {
      rowSelection = {
        getCheckboxProps: (item) => ({ disabled: item.uncheckable }),
        selectedRowKeys: selectedList,
        onSelect: (record, selected) => {
          /**
           *  체크박스 체크
           */
          if (selected) {
            onCheck(uniq([...selectedList, record.id]), record.id, selected);
          } else {
            /**
             *  체크박스 언체크
             */
            onCheck(
              filter(selectedList, (item) => item !== record.id),
              record.id,
              selected,
            );
          }
        },
        onSelectAll: (selected, selectedRows, changeRows) => {
          /**
           *  전체 선택할 경우
           */
          if (selected) {
            onCheck(uniq([...selectedList, ...changeRows.map((item) => item.id)]), null, selected);
          } else {
            /**
             *  전체 해제할 경우
             */
            onCheck(
              pullAll(
                [...selectedList],
                changeRows.map((item) => item.id),
              ),
              null,
              selected,
            );
          }
        },
      };
    }

    return rowSelection;
  };

  /**
   *  테이블 필터 callback
   */
  onFilter = (changed) => {
    const { filterOption, sortOption, pagination, getList } = this.props;
    let updatedFilterOption = {};

    if (changed) {
      updatedFilterOption = {
        ...filterOption,
        ...changed,
      };
    }

    getList({
      filterOption: updatedFilterOption,
      sortOption,
      pagination: {
        ...pagination,
        pageNum: 0,
      },
    });
  };

  /**
   *  컬럼 리스트
   *  컬럼 리스트를 만들때 onFilter 관련된 props 를 넘겨줌
   */
  setColumns = (columns) => {
    const { filterOption = {}, sortOption = {} } = this.props;

    columns = columns({
      filterOption,
      sortOption,
      onFilter: this.onFilter,
    });

    return columns;
  };

  /**
   *  drag and drop 기능을 사용시,
   *  row 를 이동할 때 발생하는 callback
   */
  moveRow = (dragId, hoverId) => {
    const { onDragEnd, list } = this.props;
    const newOrder = [...list];
    const dragTargetIndex = findIndex(newOrder, (item) => item.id === dragId);
    const hoverTargetIndex = findIndex(newOrder, (item) => item.id === hoverId);
    if (newOrder[hoverTargetIndex].rowCanDrag === false) {
      return;
    }
    const targetItem = find(newOrder, (item, index) => index === dragTargetIndex);
    newOrder.splice(dragTargetIndex, 1);
    newOrder.splice(hoverTargetIndex, 0, targetItem);

    onDragEnd({
      list: newOrder,
      idList: newOrder.map((item) => item.id),
    });
  };

  onRow = ({ id, rowCanDrag = true }, index) => {
    const { canDrag } = this.props;

    return {
      index,
      id,
      moverow: canDrag ? this.moveRow : null,
      candrag: canDrag && rowCanDrag,
    };
  };

  render() {
    const {
      className,
      emptyText = '데이터가 존재하지 않습니다.',
      bordered = true,
      showSizeChanger = true,
      pagination,
      showPagination = true,
      list,
      onCheck,
      selectedList,
      position = 'bottom',
      columns,
      canDrag,
      ...rest
    } = this.props;

    let updatedProps = {
      pagination: {
        showSizeChanger: showSizeChanger,
        pageSizeOptions: PAGE_SIZE_OPTIONS,
        position,
      },
    };

    if (canDrag) {
      updatedProps.components = {
        body: {
          row: DraggableBodyRow,
        },
      };
    }

    /**
     *  showPagination 은 기본적으로 true 인데 명시적으로 false 를 주게 되면
     *  updatedProps.pagination 을 false 를 줘서
     *  테이블에서의 pagination 을 없애도록 한다.
     */
    if (showPagination) {
      if (pagination) {
        updatedProps.pagination.current = pagination.pageNum + 1;
        updatedProps.pagination.pageSize = pagination.pageSize;
        updatedProps.pagination.total = pagination.totalCnt;
      } else {
        updatedProps.pagination.defaultPageSize = 20;
      }
    } else {
      updatedProps.pagination = false;
    }

    return (
      <Fragment>
        {pagination ? (
          <div className="_amondz-table-summary">
            <div className="left">
              <span className="list-total-cnt">
                {pagination && !isNil(pagination.totalCnt) ? (
                  <Fragment>
                    <span>{pagination.totalCnt}</span>건이 조회되었습니다.
                  </Fragment>
                ) : null}
              </span>
            </div>
          </div>
        ) : null}

        <DndProvider backend={HTML5Backend} context={window}>
          <Table
            className={cx('_amondz-table', className)}
            table-layout={'fixed'}
            {...rest}
            dataSource={list}
            columns={this.setColumns(columns)}
            rowSelection={this.rowSelection(onCheck, selectedList)}
            {...updatedProps}
            bordered={bordered}
            locale={{ emptyText }}
            onRow={this.onRow}
            onChange={this.onChange}
          />
        </DndProvider>
      </Fragment>
    );
  }
}

export default CustomTable;
